import {RATING_SCHEMES} from './constants';

export const getHealthFunctions = healthName => {
  const functions = {
    getColor: value => getHealthColor(healthName, value),
    getStatus: value => getHealthStatus(healthName, value),
    getScaledValue: value => getScaledHealthValue(healthName, value),
    getScoreStatusByNo: value => getHealthScoreOrder(healthName, value),
  };
  return functions;
};

const getHealthColor = (healthName, value) => {
  const normalizedName = healthName?.toLowerCase();

  switch (normalizedName) {
    case 'overall health score':
      if (!value && value !== 0) return 'gray';
      if (value > 77.5) return '#A6E177'; // very good
      if (value > 65) return '#50BA47'; // good
      if (value > 45) return '#F2BF3A'; // moderate
      if (value > 25) return '#FFA500'; // poor
      return '#F34848'; // very poor

    case 'heart health':
      if (!value && value !== 0) return '#gray';
      if (value >= 6.6) return '#F34848'; // very high risk
      if (value >= 3.3) return '#FF8C00'; // high risk
      if (value >= 2.39) return '#F2BF3A'; // moderate risk
      if (value >= 1.65) return '#50BA47'; // low risk
      return '#A6E177'; // very low risk

    case 'body function':
      if (!value && value !== 0) return '#gray';
      if (value >= 4.5) return '#A6E177'; // excellent
      if (value >= 3.5) return '#50BA47'; // good
      if (value >= 2.5) return '#F2BF3A'; // moderate
      if (value >= 1.5) return '#FF8C00'; // fair
      return '#F34848'; // poor

    case 'physical health':
      if (!value && value !== 0) return '#gray';
      if (value >= 4.5) return '#A6E177'; // excellent
      if (value >= 3) return '#50BA47'; // good
      if (value >= 2) return '#F2BF3A'; // moderate
      if (value >= 1) return '#FF8C00'; // fair
      return '#F34848'; // poor

    case 'metabolic health':
      if (!value && value !== 0) return '#gray';
      if (value < 25) return '#A6E177'; // very low risk
      if (value >= 25 && value < 45) return '#50BA47'; // low risk
      if (value >= 45 && value < 55) return '#F2BF3A'; // moderate risk
      if (value >= 55 && value < 77.5) return '#FF8C00'; // high risk
      return '#F34848'; // very high risk

    case 'overall disease risk':
      if (!value && value !== 0) return '#gray';
      if (value >= 29) return '#F34848'; // very high risk
      if (value >= 19) return '#FF8C00'; // high risk
      if (value >= 15) return '#F2BF3A'; // moderate risk
      if (value >= 9) return '#50BA47'; // low risk
      return '#A6E177'; // very low risk

    case 'skin health':
      if (!value && value !== 0) return '#gray';
      if (value >= 80) return '#A6E177'; // excellent
      if (value >= 65) return '#50BA47'; // good
      if (value >= 45) return '#F2BF3A'; // moderate
      if (value >= 25) return '#FFA500'; // fair
      return '#F34848'; // poor

    default:
      return RATING_SCHEMES.NORMAL.color;
  }
};

const getHealthStatus = (healthName, value) => {
  const normalizedName = healthName?.toLowerCase();

  switch (normalizedName) {
    case 'overall health score':
      if (!value && value !== 0) return 'Loading...';
      if (value > 77.5) return 'Very Good';
      if (value > 65) return 'Good';
      if (value > 45) return 'Moderate';
      if (value > 25) return 'Poor';
      return 'Very Poor';

    case 'heart health':
      if (!value && value !== 0) return 'Loading...';
      if (value >= 6.6) return 'Very High Risk';
      if (value >= 3.3) return 'High Risk';
      if (value >= 2.39) return 'Moderate Risk';
      if (value >= 1.65) return 'Low Risk';
      return 'Very Low Risk';

    case 'body function':
      if (!value && value !== 0) return 'Loading...';
      if (value >= 4.5) return 'Excellent';
      if (value >= 3.5) return 'Good';
      if (value >= 2.5) return 'Moderate';
      if (value >= 1.5) return 'Fair';
      return 'Poor';

    case 'physical health':
      if (!value && value !== 0) return 'Loading...';
      if (value >= 4.5) return 'Excellent';
      if (value >= 3) return 'Good';
      if (value >= 2) return 'Moderate';
      if (value >= 1) return 'Fair';
      return 'Poor';

    case 'metabolic health':
      if (!value && value !== 0) return 'Loading...';
      if (value < 25) return 'Very Low Risk';
      if (value >= 25 && value < 45) return 'Low Risk';
      if (value >= 45 && value < 55) return 'Moderate Risk';
      if (value >= 55 && value < 77.5) return 'High Risk';
      return 'Very High Risk';

    case 'overall disease risk':
      if (!value && value !== 0) return 'Loading...';
      if (value >= 29) return 'Very High Risk';
      if (value >= 19) return 'High Risk';
      if (value >= 15) return 'Moderate Risk';
      if (value >= 9) return 'Low Risk';
      return 'Very Low Risk';

    case 'skin health':
      if (!value && value !== 0) return 'Loading...';
      if (value >= 80) return 'Excellent';
      if (value >= 65) return 'Good';
      if (value >= 45) return 'Moderate';
      if (value >= 25) return 'Fair';
      return 'Poor';

    default:
      return RATING_SCHEMES.NORMAL.text;
  }
};

const getScaledHealthValue = (healthName, value) => {
  // Scale value to 0-100 if needed
  return Math.min(100, Math.max(0, value));
};

const getHealthScoreOrder = (healthName, value) => {
  // Return a numeric order for sorting (lower is worse)
  const normalizedName = healthName?.toLowerCase();

  switch (normalizedName) {
    case 'overall health score':
      if (!value && value !== 0) return 0;
      if (value > 77.5) return 5;
      if (value > 65) return 4;
      if (value > 45) return 3;
      if (value > 25) return 2;
      return 1;

    case 'heart health':
      if (!value && value !== 0) return 0;
      if (value >= 6.6) return 1;
      if (value >= 3.3) return 2;
      if (value >= 2.39) return 3;
      if (value >= 1.65) return 4;
      return 5;

    case 'body function':
      if (value == null || value === '') return 0;
      if (value >= 4.5) return 5;
      if (value >= 3.5) return 4;
      if (value >= 2.5) return 3;
      if (value >= 1.5) return 2;
      return 1;

    case 'physical health':
      if (!value && value !== 0) return 0;
      if (value >= 4.5) return 5;
      if (value >= 3) return 4;
      if (value >= 2) return 3;
      if (value >= 1) return 2;
      return 1;

    case 'metabolic health':
      if (!value && value !== 0) return 0;
      if (value < 25) return 5;
      if (value >= 25 && value < 45) return 4;
      if (value >= 45 && value < 55) return 3;
      if (value >= 55 && value < 77.5) return 2;
      return 1;

    case 'overall disease risk':
      if (!value && value !== 0) return 0;
      if (value >= 29) return 1;
      if (value >= 19) return 2;
      if (value >= 15) return 3;
      if (value >= 9) return 4;
      return 5;

    case 'skin health':
      if (!value && value !== 0) return 0;
      if (value >= 80) return 5;
      if (value >= 65) return 4;
      if (value >= 45) return 3;
      if (value >= 25) return 2;
      return 1;

    default:
      return value;
  }
};

// Keep your existing ratingsToObj function as is - it looks correct

export const ratingsToObj = (ratings, value, diastolicValue = null) => {
  if (!ratings) return null;

  // Handle special rating schemes
  if (ratings.scheme === 'bmi') {
    if (value < ratings.underweight[1]) return {...RATING_SCHEMES.LOW, text: 'Underweight'};
    if (value < ratings.normal[1]) return {...RATING_SCHEMES.OPTIMAL, text: 'Normal'};
    if (value < ratings.overweight[1]) return {...RATING_SCHEMES.ELEVATED, text: 'Overweight'};
    return {...RATING_SCHEMES.HIGH, text: 'Obese'};
  }

  if (ratings.scheme === 'age') {
    if (value < ratings.younger[1]) return {...RATING_SCHEMES.OPTIMAL, text: 'Younger'};
    if (value < ratings.normal[1]) return {...RATING_SCHEMES.NORMAL, text: 'Normal'};
    return {...RATING_SCHEMES.ELEVATED, text: 'Older'};
  }

  if (ratings.scheme === 'risk') {
    if (ratings['very low'] && value <= ratings['very low'][1]) {
      return {...RATING_SCHEMES.OPTIMAL, text: 'Very Low'};
    }
    if (ratings.low && value <= ratings.low[1]) {
      return {...RATING_SCHEMES.NORMAL, text: 'Low', color: '#4CAF50'};
    }
    if (ratings.moderate && value <= ratings.moderate[1]) {
      return {...RATING_SCHEMES.ELEVATED, text: 'Moderate'};
    }
    if (ratings.high && value <= ratings.high[1]) {
      return {...RATING_SCHEMES.HIGH, text: 'High'};
    }
    if (ratings['very high']) {
      return {...RATING_SCHEMES.HIGH, text: 'Very High', color: '#D32F2F'};
    }
  }

  // Handle blood pressure with diastolic
  if (diastolicValue !== null && ratings.diastolicbpRatings) {
    const systolicRating = getRatingFromRange(ratings, value);
    const diastolicRating = getRatingFromRange(ratings.diastolicbpRatings, diastolicValue);
    // Return the worse of the two ratings
    return getWorseRating(systolicRating, diastolicRating);
  }

  // Standard rating calculation
  return getRatingFromRange(ratings, value);
};

const getRatingFromRange = (ratings, value) => {
  for (const [key, range] of Object.entries(ratings)) {
    if (Array.isArray(range) && value >= range[0] && value <= range[1]) {
      return getRatingScheme(key);
    }
  }
  return RATING_SCHEMES.NORMAL;
};

const getRatingScheme = key => {
  const keyMap = {
    optimal: RATING_SCHEMES.OPTIMAL,
    normal: RATING_SCHEMES.NORMAL,
    elevated: RATING_SCHEMES.ELEVATED,
    high: RATING_SCHEMES.HIGH,
    low: RATING_SCHEMES.LOW,
    'very low': {...RATING_SCHEMES.LOW, text: 'Very Low'},
    'high normal': {...RATING_SCHEMES.ELEVATED, text: 'High Normal'},
    'low normal': {...RATING_SCHEMES.NORMAL, text: 'Low Normal'},
    'very high': {...RATING_SCHEMES.HIGH, text: 'Very High'},
  };

  return keyMap[key.toLowerCase()] || RATING_SCHEMES.NORMAL;
};

const getWorseRating = (rating1, rating2) => {
  const severityOrder = ['optimal', 'normal', 'elevated', 'high'];
  const index1 = severityOrder.indexOf(rating1.text.toLowerCase());
  const index2 = severityOrder.indexOf(rating2.text.toLowerCase());

  return index1 > index2 ? rating1 : rating2;
};

// Updated skin health calculations based on your working code
export const calculateSkinHealthScore = (physicalHealthData, age) => {
  try {
    // Use 'skynResult' as per your working code
    const skinResult = JSON.parse(localStorage.getItem('skynResult') || '{}');
    const facialSkinAge = physicalHealthData?.facialSkinAge || age;

    let score = 50; // Base score

    // Age difference impact
    const ageDiff = facialSkinAge - age;
    if (ageDiff > 0) {
      score -= Math.min(30, ageDiff * 3);
    } else {
      score += Math.min(15, Math.abs(ageDiff) * 1.5);
    }

    // Acne impact
    if (skinResult.acne) {
      switch (skinResult.acne.toLowerCase()) {
        case 'clear':
          score += 20;
          break;
        case 'mild':
          score += 10;
          break;
        case 'moderate':
          score -= 5;
          break;
        case 'severe':
          score -= 15;
          break;
      }
    }

    // Skin tone impact
    if (skinResult.tone) {
      const toneValue = parseInt(skinResult.tone);
      score += (toneValue - 5) * 3;
    }

    // Skin type impact
    if (skinResult.type) {
      switch (skinResult.type.toLowerCase()) {
        case 'normal':
          score += 15;
          break;
        case 'combination':
          score += 10;
          break;
        case 'dry':
        case 'oily':
          score -= 5;
          break;
        case 'sensitive':
          score -= 10;
          break;
      }
    }

    return Math.max(0, Math.min(100, Math.round(score)));
  } catch (error) {
    console.error('Error calculating skin health score:', error);
    return 50;
  }
};

export const getSkinDataFromStorage = key => {
  try {
    // Use 'skynResult' as per your working code
    const skinData = JSON.parse(localStorage.getItem('skynResult') || '{}');
    if (key) return skinData[key];
    return skinData;
  } catch {
    return key ? null : {};
  }
};

export const getAcneSeverityScore = acneData => {
  if (!acneData) return 70; // Default moderate score

  const severityMap = {
    clear: 95,
    'Very Low': 95,
    Low: 85,
    mild: 85,
    Moderate: 50,
    moderate: 50,
    Severe: 20,
    severe: 20,
    'Very Severe': 10,
  };

  return severityMap[acneData] || 70;
};

export const getSkinToneScore = toneData => {
  if (!toneData) return 75;

  const tone = parseInt(toneData);
  if (isNaN(tone)) return 75;

  // Convert 1-10 scale to health score
  return Math.min(100, Math.max(0, tone * 10));
};

export const getSkinTypeScore = typeData => {
  if (!typeData) return 75;

  const typeMap = {
    Normal: 95, // Optimal balance
    normal: 95,
    Combination: 80, // Good balance
    combination: 80,
    Dry: 60, // Needs moisture
    dry: 60,
    Oily: 60, // Needs oil control
    oily: 60,
    Sensitive: 50, // Needs special care
  };

  return typeMap[typeData] || 75;
};

// Calculate health metrics
export const calculateHealthMetrics = {
  skinHealthScore: calculateSkinHealthScore,
  skinClarityScore: () => getAcneSeverityScore(getSkinDataFromStorage('acne')),
  skinToneScore: () => getSkinToneScore(getSkinDataFromStorage('tone')),
  skinBalanceScore: () => getSkinTypeScore(getSkinDataFromStorage('type')),
};
